import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { ESPACO } from "../data/content";
import useSiteContent from "../hooks/useSiteContent";
import { X, Check, ArrowRight } from "lucide-react";

export default function EspacoPage() {
  const [active, setActive] = useState(null);
  const cms = useSiteContent();
  const e = cms.espaco || {};
  const features = (e.features && e.features.length ? e.features : ESPACO.features) || [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="bg-[#FAFAFA]">
      <Header />

      {/* Hero banner */}
      <section className="relative pt-20" data-testid="espaco-page-hero">
        <div className="relative h-[52vh] md:h-[64vh] overflow-hidden">
          <img
            src={ESPACO.hero}
            alt="O espaço INN.PACT Clínica de Imagem em Leiria, entrada e ambiente principal"
            className="w-full h-full object-cover"
            fetchPriority="high"
            loading="eager"
            decoding="sync"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 flex flex-col justify-end">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-7xl mx-auto w-full px-6 md:px-12 pb-12 text-white"
            >
              <p className="label-editorial text-clay-soft mb-4">O Espaço · Leiria</p>
              <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none">
                O nosso <span className="italic">espaço</span>
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro + features */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">
              {e.page_heading_before || "Um espaço que sente como"}{" "}
              <span className="italic text-clay">{e.page_heading_highlight || "casa"}</span>
              {e.page_heading_after || "."}
            </h2>
            <p className="mt-8 text-black/60 font-light leading-relaxed text-lg max-w-2xl whitespace-pre-line">{e.intro || ESPACO.intro}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <ul className="space-y-4">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-black/70 font-light border-t border-black/10 pt-4">
                  <span className="mt-0.5 w-6 h-6 flex items-center justify-center bg-clay/10 text-clay shrink-0">
                    <Check size={14} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Gallery grid */}
      <section className="pb-24 md:pb-32" data-testid="espaco-gallery">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {ESPACO.gallery.map((img, i) => (
              <motion.button
                key={img.src}
                type="button"
                onClick={() => setActive(img)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
                data-testid={`espaco-gallery-item-${i}`}
                className={`gallery-item group relative overflow-hidden ${
                  i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"
                }`}
              >
                <img
                  src={img.src}
                  alt={img.caption}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 label-editorial text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {img.caption}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-clay text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-none max-w-xl">
            Venha conhecer-nos <span className="italic">pessoalmente</span>.
          </h2>
          <Link
            to="/#marcacoes"
            data-testid="espaco-cta-booking"
            className="inline-flex items-center gap-2 label-editorial bg-white text-clay px-10 py-4 hover:bg-white/90 transition-colors duration-300 whitespace-nowrap"
          >
            Agendar Marcação <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />

      {/* Lightbox */}
      {active && (
        <div
          data-testid="espaco-lightbox"
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-10 bg-black/90 animate-fade-in"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="espaco-lightbox-close"
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            onClick={() => setActive(null)}
            aria-label="Fechar"
          >
            <X size={28} />
          </button>
          <img
            src={active.src}
            alt={active.caption}
            className="max-w-full max-h-full object-contain"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
